import React, { useEffect, useRef, useState } from 'react';
import Card from 'react-bootstrap/Card';
import InputFieldsComponent from '../../../../components/common/InputFields';
import Button from '../../../../components/common/Button';
import './CreateNewChild.css';
import Time from '@pages/UserManagement/CreateNewUser/VerificationCard/time';
import Spinner from '@components/common/Spinner';
import { getData, postDataJSON } from "../../../../services/CommonApiService";
import endpoints from '@config/endpoints';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@store/reducers';
import { useDispatch } from 'react-redux';
import { updateChild } from '@store/actions/beneficiaryAction';
import { encode, decode, encodeWithKeyAndShift, decryptData, decodeWithKeyAndShift } from '../../../../utils/base64Utils';
import InfoBar from '@components/common/InfoBars';
import { errorLogger } from '@utils/applicationLogUtils';
import { benef_const, benef_error_const } from '@constants/beneficiaryConstant';
import { sendChildOTPViaAadhaar, verifyChildOTPViaAadhaar } from '@services/childApiServices';
import { CHILD_ERROR_MESSAGES, child_otp_const } from '../Child/childConstants';
import { formatNumber, handleError } from '@utils/childUtils';
import { startOtpCooldown, isOtpRateLimited, isOtpRateLimitedError, useOtpCooldown } from '@hooks/useOtpCooldown';
import { otpCooldownMessage } from '@constants/appConstants';

interface OtpcardcomponentProps {
  otpLabel: string;
  onClickSubmit: (value: any, res: any, isChildLogin?: boolean) => void;
  header: string;
  flow:string;
  showOtpSpinner?:boolean;
  isChildKYC: boolean;
  isChildLogin: boolean;
  aadhaarTxnId?: string;
  aadhaarOtpResponseMessage?: string;
}

const ChildOtpModal: React.FC<OtpcardcomponentProps> = ({
  otpLabel,
  onClickSubmit,
  header,
  flow,
  showOtpSpinner,
  isChildKYC,
  isChildLogin,
  aadhaarTxnId,
  aadhaarOtpResponseMessage,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [index, setIndex] = useState<number>(0);
  const [selectedInputFieldValue, setSelectedInputFieldValue] = useState<string[]>([]);
  const [onSubmit, setOnSubmit] = useState<boolean>(false);
  const [isTimeFinished, setIsTimeFinished] = useState<boolean>(false);
  const [otpWrong, setOtpWrong] = useState<string>('');
  const [resetKey, setResetKey] = useState<number>(0);
  const [retry, setRetry] = useState<number>(benef_const.defalut_retry);
  const [retryMessage, setRetryMessage] = useState<string>('');
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const parentAbha = useSelector((state: RootState) => state.beneficiary?.parentAbha);
  const childKYCDetails = useSelector((state: RootState) => state.beneficiary?.childKYCDetails);
  const childBalAbha = useSelector((state: RootState) => state.beneficiary?.childBalAbha);
  const childToken = useSelector((state: RootState) => state.beneficiary?.childToken);
  const [tknId, setTknId] = useState<string>('');
  const [abhaData, setAbhaData] = useState<any>({});
  const [otpResponseMessage, setOtpResponseMessage] = useState('');
  const [otpError, setOtpError] = useState<boolean>(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState<string>('Something went wrong. Please try again.');
  const { secondsLeft, isCoolingDown } = useOtpCooldown(abhaData?.abhaNumber?.replace(/-/g, ''));

  useEffect(() => {
    setTknId(aadhaarTxnId || '');
    setOtpResponseMessage(aadhaarOtpResponseMessage || '');
  }, [aadhaarTxnId, aadhaarOtpResponseMessage]);

  useEffect(() => {
    if(retry < 0) {
      setOnSubmit(false);
    }
    else if (selectedInputFieldValue.length === 6 && selectedInputFieldValue.every((str) => str)) {
      setOnSubmit(true);
    } else {
      setOnSubmit(false);
    }
  }, [selectedInputFieldValue]);

  useEffect(() => {
    if((isChildLogin && isChildKYC) || (!isChildLogin && !isChildKYC)){
      const abhaNumber = isChildLogin ? childBalAbha : parentAbha;
      const formattedAbhaNum = formatNumber(abhaNumber);
      const data = { abhaNumber: formattedAbhaNum };
      setAbhaData(data);
      fetchAbha(data);
    }
  }, [parentAbha, isChildLogin]);

  const fetchAbha = async (data: any) => {
    try {
      const encodedPayload = encodeWithKeyAndShift(JSON.stringify(data));
      const response = await postDataJSON<any>(`${endpoints.sendOtpToAbhaToVerifyAbha}`, { data: encodedPayload },);
      const decodedData = JSON.parse(decodeWithKeyAndShift(response?.data));
      const result = decodedData;
      if(result.txnId) {
        setOtpResponseMessage(result?.message);
        setTknId(result.txnId);
        setShowSpinner(false);
      }
      else{
        setOtpError(true);
        setShowSpinner(false);
        setOtpErrorMessage(result.message)
      }
    } catch (error: any) {
      if (isOtpRateLimitedError(error)) {
        startOtpCooldown(data?.abhaNumber?.replace(/-/g, ''));
        handleError(otpCooldownMessage);
        setShowSpinner(false);
        return;
      }
      setOtpError(true);
      errorLogger(error?.response?.data||error?.response||error)
    }
  };

  const handleChildKYC = async () => {
    try {
      const data = {
        aadhaar: childKYCDetails?.aadhaarNumber,
        token: childToken,
      };
      const response = await sendChildOTPViaAadhaar(data);
      const decodedData = JSON.parse(decodeWithKeyAndShift(response?.data));
      const result = decodedData;
      if (result.txnId) {
        setOtpResponseMessage(result?.message);
        setTknId(result.txnId);
        setShowSpinner(false);
      } else if (result?.authResult === child_otp_const.failed) {
        setOtpWrong(child_otp_const.invalidOtp);
        setShowSpinner(false);
      } else if (result?.message?.includes(child_otp_const.runtimeError)) {
        handleError(`${result?.code} - ${result.message}`, benef_error_const.urlNotFound);
      } else if (result?.message?.includes(child_otp_const.seconds30)) {
        handleError(result?.message);
      } else if (result?.message?.includes(child_otp_const.multipleOtps)) {
        handleError(result?.message, benef_error_const.multipleOTP);
      } else if (result?.message?.includes(child_otp_const.missingCredentials)) {
        handleError(`${result?.code} - ${result?.description}`);
      } else if (result?.message?.includes(child_otp_const.tooManyRequests)) {
        handleError(`${result?.message}`);
      } else if (result?.message?.includes(child_otp_const.gatewayUnavailable)) {
        handleError(result?.message);
      } else if (result?.loginId?.includes(child_otp_const.invalidLoginId)) {
        handleError(result?.loginId, benef_error_const.provideCorrectAadhaar);
      } else {
        setOtpError(true);
        setShowSpinner(false);
        setOtpErrorMessage(result.message);
      }
    } catch (error: any) {
      setOtpError(true);
      errorLogger(error?.response?.data || error?.response || error);
    }
  };

  const handleInputFieldChange = (value: string, index: number, event?: React.KeyboardEvent) => {
    const newSelectedInputField = [...selectedInputFieldValue];
    
    if (event?.key === "Backspace") {
      if (!value) {
        if (index > 0) {
          document.getElementById(`otp-input-${index - 1}`)?.focus();
        }
      } else {
        newSelectedInputField[index] = '';
        setSelectedInputFieldValue(newSelectedInputField);
      }
    } else if (value.trim()) {
      newSelectedInputField[index] = value.trim();
      setSelectedInputFieldValue(newSelectedInputField);
      if (index < 5) {
        document.getElementById(`otp-input-${index + 1}`)?.focus();
      }
    }
  };

  const onClickProceed = async () => {
    if (selectedInputFieldValue.length === 6) {
      const value = selectedInputFieldValue.join('');
      setShowSpinner(true);
      try {
        if(isChildKYC &&  !isChildLogin) {
          handleChildOTPSubmit(value);
        } else {
          const data = { "otp": value, "txnId": tknId };
          const encodedPayload = encodeWithKeyAndShift(JSON.stringify(data));
          const otpResponse = await postDataJSON<any>(`${endpoints.verifyOtpToAbhaToVerifyAbha}`, {data: encodedPayload});
          const decodedData = JSON.parse(decodeWithKeyAndShift(otpResponse.data));
          const res = decodedData?.[0];
          dispatch(updateChild('childToken', res?.token));
          if (res?.authResult === 'success') {
            setShowSpinner(false);
            onClickSubmit(value, res, true);
          } else {
            setOtpResponseMessage(res?.message);
            setOtpWrong('Invalid OTP');
            setShowSpinner(false);
          }
        }
      } catch (error:any) {
        errorLogger(error?.response?.data||error?.response||error)
        setOtpWrong('Invalid OTP');
        setShowSpinner(false);
      }
    }

  };

  const handleChildOTPSubmit = async (value:any) => {
    try {
      const childData = { "otp": value, "txnId": tknId, token: childToken,};
      const otpResponse = await verifyChildOTPViaAadhaar(childData);
      const res = JSON.parse(decodeWithKeyAndShift( otpResponse?.data));
      if (res?.authResult === child_otp_const.success) {
        setShowSpinner(false);
        onClickSubmit(value, res, false);
      } else if (res?.authResult === child_otp_const.failed) {
        setOtpWrong(child_otp_const.invalidOtp);
        setShowSpinner(false);
      } else if (res?.message?.includes(child_otp_const.runtimeError)) {
        handleError(`${res?.code} - ${res.message}`, benef_error_const.urlNotFound);
      } else if (res?.message?.includes(child_otp_const.seconds30)) {
        handleError(res?.message);
      } else if (res?.message?.includes(child_otp_const.multipleOtps)) {
        handleError(res?.message, benef_error_const.multipleOTP);
      } else if (res?.message?.includes(child_otp_const.missingCredentials)) {
        handleError(`${res?.code} - ${res?.description}`);
      } else if (res?.message?.includes(child_otp_const.tooManyRequests)) {
        handleError(`${res?.message}`);
      } else if (res?.message?.includes(child_otp_const.gatewayUnavailable)) {
        handleError(res?.message);
      } else if (res?.loginId?.includes(child_otp_const.invalidLoginId)) {
        handleError(res?.loginId, benef_error_const.provideCorrectAadhaar);
      } else {
        handleError(res?.message ?? benef_error_const.genericAbdmMessage);
      }
      setShowSpinner(false);
    } catch (error:any) {
      setShowSpinner(false);
      const decodedData = JSON.parse(decodeWithKeyAndShift(error));
      const res = decodedData;
      let catchErrorMessage = '';
      if(res?.message && res?.code){
          catchErrorMessage =`${res?.code} - ${res?.message}`
      }else if(res?.message && !res?.code){
          catchErrorMessage =`${res?.message}`
      }else if(res?.loginId){
          catchErrorMessage =`${res?.loginId}`
      }else if(res?.Aadhaar){
          catchErrorMessage =`${res?.Aadhaar}`
      }else{
          catchErrorMessage = benef_error_const.genericAbdmMessage
      } 
      handleError(catchErrorMessage);
      errorLogger(res, CHILD_ERROR_MESSAGES.CHILD_API_ERROR);
    }
  }

  const restartTimer = async () => {
    if (retry <= 0) {
      setRetryMessage('OTP limit reached, Please try after sometime');
    } else {
      setShowSpinner(true);
      setSelectedInputFieldValue([]);
      setIndex(0);
      try {
        if(isChildKYC && !isChildLogin) {
          handleChildKYC();
        } else {
          const encodedPayload = encodeWithKeyAndShift(JSON.stringify(abhaData));
          const response = await postDataJSON<any>(`${endpoints.sendOtpToAbhaToVerifyAbha}`, {data:encodedPayload});
          const decodedData = JSON.parse(decodeWithKeyAndShift(response.data));
          const result = decodedData;
          if(result.txnId) {
            setOtpResponseMessage(result?.message);
            setTknId(result.txnId);
            setShowSpinner(false);
          }
          else{
            setOtpError(true);
            setShowSpinner(false);
            setOtpErrorMessage(result.message)
            if (isOtpRateLimited(result?.message)) {
              startOtpCooldown(abhaData?.abhaNumber?.replace(/-/g, ''));
            }
          }
        }
      } catch (error:any) {
        if (isOtpRateLimitedError(error)) {
          startOtpCooldown(abhaData?.abhaNumber?.replace(/-/g, ''));
          handleError(otpCooldownMessage);
          setShowSpinner(false);
          return;
        }
        errorLogger(error?.response?.data||error?.response||error)
        setShowSpinner(false);
      }
      setResetKey((prevKey) => prevKey + 1);
      setIsTimeFinished(false);
      setRetry(retry - 1);
      setOtpWrong('');
      setRetryMessage('');
      setShowSpinner(false);
    }
  };

  const handleTimerEnd = () => {
    setIsTimeFinished(true);
  };

  const handleKeyDown = (event:React.KeyboardEvent, index:any) => {
    handleInputFieldChange(selectedInputFieldValue[index] || '', index, event);
  }

  return (
    <div className="otp_card_css">
      <Card className="child_otp_entire_card">
        <Card.Header className="login_card_header">{header}</Card.Header>
        <Card.Body className="login_card_body">
        <h2 style={{ fontSize: '14px', color:'#797979'}}>{otpResponseMessage}</h2> 
          <label className="otp_card_container_label">{otpLabel}</label>
          <form autoComplete="off">
            <div className="otp_card_container">
              {Array.from({ length: 6 }).map((_, index) => (
                <div className="otp_card_container_text" key={index}>
                  <InputFieldsComponent
                    className={selectedInputFieldValue[index] ? 'input-fields-has-value' : 'input-fields'}
                    placeholder="_"
                    inputType="entered"
                    type="number"
                    isDisabled={false}
                    value={selectedInputFieldValue[index] || ''}
                    inputId={`otp-input-${index}`}
                    onChange={(value: string) => handleInputFieldChange(value, index)}
                    maximumLength={1}
                    onkeydown={(event:any) => handleKeyDown(event, index)}
                  />
                </div>
              ))}
            </div>
          </form>
          <div className="otp-resend">
            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
              <h2 style={{ fontSize: '14px' }}>Didn't receive an OTP</h2>
              <span>
                {isTimeFinished ? (
                  <Button
                    onClick={restartTimer}
                    disabled={isCoolingDown}
                    id=""
                    buttonName="Resend OTP"
                    colorType="save"
                    icon=""
                    iconPosition=""
                  />
                ) : (
                  <div className="divbutton">
                    <Button
                      onClick={() => {}}
                      disabled={true}
                      id=""
                      buttonName={`${otpWrong} Resend OTP in`}
                      colorType="save"
                      icon=""
                      iconPosition=""
                    />
                  </div>
                )}
              </span>
              <span>
                <Time startTime={90} resetKey={resetKey} onTImeEnd={handleTimerEnd} />
              </span>
            </div>
            <span className="no_of_retries_resend_otp">Retries left: {retry >= 0 ? retry : 0}</span><br></br>
            <span className="no_of_retries_resend_otp">{retryMessage}</span>
            {isCoolingDown && (
              <p style={{ color: '#b42318', fontSize: '12px', margin: '8px 0 0' }}>
                You can request for new OTP after {secondsLeft} seconds
              </p>
            )}
          </div>
          <span>{(showSpinner || showOtpSpinner) && <><span>Loading...</span><Spinner /></>}</span>
          {otpError && <div className='child-otp-error'>
            <InfoBar
            variant="danger"
            message={otpErrorMessage}
            className="input_field_class"
          />
          </div>}
        </Card.Body>
        <Card.Footer className="login_card_footer">
          <Button
            buttonName="Submit"
            colorType={onSubmit ? 'primary' : 'disabled'}
            id=""
            onClick={onClickProceed}
            disabled={!onSubmit}
          />
        </Card.Footer>
      </Card>
    </div>
  );
};

export default ChildOtpModal;

